import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Grid, Typography } from '@mui/material';
import { useId, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  EditableLegalClientSchema,
  IEditableLegalClient,
} from '../../../../../Interfaces/IEditLegalClient';
import {
  getEmptyLegalClient,
  ILegalClient,
} from '../../../../../Interfaces/ILegalClient';
import { ILegalTransactionRelationship } from '../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { getEmptyNaturalPerson } from '../../../../../Interfaces/INaturalPerson';
import { useAlert } from '../../../../core/AlertContext';

import { CustomeSlide } from '../../../../core/CustomeSlide';
import { useDialogContext } from '../../../../core/DialogPresenter/DialogContext';
import { useRegisterDialogPage } from '../../../../core/DialogPresenter/useRegisterDialogPage';
import { DialogResponsive } from '../../../../core/DialogResponsive';
import { EditLegalClient } from '../../../../LegalClient/EditLegalClient';
import { TableLegalClient } from '../../../../LegalClient/TableLegalClient';
import { useLegalClientStore } from '../../generic/legalClientStore';
import { NavRow } from '../../generic/NavRow';
import {
  isFamilyRelationship,
  isMarriageRelationship,
} from '../../Inherit/utilities';
import NewFamilyTree from '../components/NewFamilyTree/NewFamilyTree';

export type ITestamentRelatives = {
  relationshipArray: ILegalTransactionRelationship[];
  setRelationshipArray: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationship[]>
  >;
};

export const TestamentRelatives: React.FC<ITestamentRelatives> = ({
  relationshipArray,
  setRelationshipArray,
}) => {
  const id = useId();
  const { handleNext, handleBack } = useDialogContext();
  const { showAlert } = useAlert();

  const pageNumber = useRegisterDialogPage(id, {
    title: 'Angehörige',
  });

  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [isOpenEdit, setIsOpenEdit] = useState(false);

  const [legalClientArray, putLegalClient, removeLegalClient] =
    useLegalClientStore(state => [
      state.legalClientArray,
      state.putLegalClient,
      state.removeLegalClient,
    ]);

  const isOpen = isOpenAdd || isOpenEdit;

  const onClickNew = () => {
    methods.reset({
      ...getEmptyLegalClient(),
      ...getEmptyNaturalPerson(),
    });
    setIsOpenAdd(true);
  };

  const onClickEdit = (legalClient: ILegalClient) => {
    methods.reset({ ...legalClient } as IEditableLegalClient);
    setIsOpenEdit(true);
  };

  const onClose = () => {
    setIsOpenAdd(false);
    setIsOpenEdit(false);
  };

  const onSave = (legalClient: ILegalClient) => {
    putLegalClient(legalClient);
    onClose();
  };

  const localHandleNext = () => {
    const relativesRelationships = relationshipArray.filter(
      rel => isFamilyRelationship(rel) || isMarriageRelationship(rel)
    );

    if (relativesRelationships.length === 0) {
      showAlert({
        severity: 'warning',
        text: 'Bitte fügen Sie mindestens eine verwandte Person oder Ehepartner hinzu, um fortzufahren.',
      });
      return;
    }

    handleNext();
  };

  const methods = useForm<IEditableLegalClient>({
    mode: 'onChange',
    defaultValues: {
      ...getEmptyLegalClient(),
      ...getEmptyNaturalPerson(),
    },
    resolver: zodResolver(EditableLegalClientSchema),
  });

  const onSubmit = methods.handleSubmit(data => {
    onSave(data as ILegalClient);
  });

  return pageNumber === null ? null : (
    <CustomeSlide key={id} pageNumber={pageNumber} appear={false}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant='h4' sx={{ mb: 3 }}>
            Angehörige
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography>
            In diesem Abschnitt können Sie die Verwandten des Erblassers
            angeben. Diese Informationen sind wichtig für die rechtliche
            Gültigkeit des Testaments.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <NewFamilyTree
            relationshipArray={relationshipArray}
            setRelationshipArray={setRelationshipArray}
          />
        </Grid>

        <DialogResponsive
          open={isOpen}
          onClose={onClose}
          title={isOpenAdd ? 'Neue Person' : 'Person bearbeiten'}
          maxWidth='lg'
        >
          <Box>
            <FormProvider {...methods}>
              <form id='edit-legal-client-form' onSubmit={onSubmit} noValidate>
                <EditLegalClient
                  disableCompanyLegalClient
                  deathOptions={{
                    deathInfoMode: 'toggle',
                    requireDate: false,
                  }}
                />
                <NavRow
                  handleBack={onClose}
                  backButtonText='Abbrechen'
                  nextButtonText='Übernehmen'
                />
              </form>
            </FormProvider>
          </Box>
        </DialogResponsive>

        <Grid item xs={12}>
          <TableLegalClient
            legalClientArray={legalClientArray}
            onRemoveClick={legalClient =>
              removeLegalClient(legalClient.idLegalClient)
            }
            onClickNew={onClickNew}
            onRowClick={onClickEdit}
            title='Angehörige'
          />
        </Grid>

        <NavRow handleBack={handleBack} handleNext={localHandleNext} />
      </Grid>
    </CustomeSlide>
  );
};
